import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import clsx from 'clsx';

import { ChevronToggle } from '@/components/common-components/ChevronToggle';
import CustomTooltip from '@/components/common-components/CustomTooltip/CustomTooltip';
import InputComponent from '@/components/common-components/InputComponent/InputComponent';
import ModalCommonButtonsContainer from '@/components/common-components/ModalComponent/ModalCommonButtonsContainer/ModalCommonButtonsContainer';
import RequiredAsterisk from '@/components/common-components/RequiredAsterisk/RequiredAsterisk';
import ShortString from '@/components/common-components/ShortString/ShortString';
import TickIcon2 from '@/components/Icons/icons/TickIcon2';

interface DelayOption {
  label: string;
  value: string | number;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

interface FrequencyInputProps {
  label?: string;
  required?: boolean;
  value?: string;
  placeholder?: string;
  leftIcon?: React.ReactNode;
  className?: string;
  stringLength?: number;
  options: DelayOption[];
  onChange: (value: string) => void;
  maxCustomDelay?: number;
  minCustomDelay?: number;
  maxCustomDelayErrorMessage?: string;
  minCustomDelayErrorMessage?: string;
}

const FrequencyInput: React.FC<FrequencyInputProps> = ({
  label,
  required = false,
  value,
  placeholder = 'Select frequency',
  leftIcon,
  className = '',
  stringLength,
  options,
  onChange,
  maxCustomDelay = 180,
  minCustomDelay = 7,
  maxCustomDelayErrorMessage = 'Maximum delay can be of 180 days.',
  minCustomDelayErrorMessage = 'Minimum delay can be of 7 days.',
}) => {
  const [delayDropdown, setDelayDropdown] = useState<HTMLDivElement | null>(null);
  const [selectedDelay, setSelectedDelay] = useState<string | undefined>(value);
  const [tempCustomDelay, setTempCustomDelay] = useState<string | null>(null);
  const [isCustomDelay, setIsCustomDelay] = useState(false);

  const divElementRef = useRef<HTMLDivElement>(null);
  const customDelaySubmitButtonRef = useRef<HTMLDivElement>(null);

  const isMaxLimitReachedForCustomDelay = tempCustomDelay ? Number(tempCustomDelay) > maxCustomDelay : false;
  const isMinLimitReachedForCustomDelay = tempCustomDelay ? Number(tempCustomDelay) < minCustomDelay : false;
  const getSelectedValue = () => {
    const option = options.find((opt) => opt.value.toString() === selectedDelay);
    return option ? option.label : `${selectedDelay === '1' ? '1 day' : `${selectedDelay} days`}` || '';
  };

  // Handle value changes
  useEffect(() => {
    if (selectedDelay !== value) {
      onChange(selectedDelay || '');
    }
  }, [selectedDelay, onChange, value]);

  useEffect(() => {
    if (selectedDelay) {
      const checkIfDelayExistsInOptions = options.find(
        (delay) => delay.value?.toString() === selectedDelay?.toString()
      );
      if (checkIfDelayExistsInOptions) {
        setIsCustomDelay(false);
      } else {
        setTempCustomDelay(selectedDelay);
      }
    }
  }, [selectedDelay]);
  return (
    <div>
      {label && (
        <label className="text-sm text-subdued mb-1 flex flex-start gap-1">
          {label}
          {required && <RequiredAsterisk />}
        </label>
      )}
      <div
        ref={divElementRef}
        onClick={(e) => setDelayDropdown(e.currentTarget)}
        className={`new-single-select-container ${delayDropdown ? 'select-active' : ''} ${className}`}>
        <span className="flex flex-start gap-2">
          {leftIcon && <span className="flex items-center">{leftIcon}</span>}
          {selectedDelay ? (
            <ShortString
              name={getSelectedValue()}
              stringLength={stringLength || 25}
              placement="bottom"
              style={{ fontSize: '16px' }}
            />
          ) : (
            <span className="text-base text-disabled">{placeholder}</span>
          )}
        </span>
        <ChevronToggle toggle={Boolean(delayDropdown)} />
      </div>

      <Popover
        open={Boolean(delayDropdown)}
        anchorEl={delayDropdown}
        onClose={() => setDelayDropdown(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            backgroundColor: 'white',
            borderRadius: 0,
            zIndex: 99999,
            width: divElementRef?.current?.clientWidth || 296,
            height: 203,
            overflow: 'auto',
            padding: '8px 4px 8px 8px',
          },
        }}>
        {options?.map((option, i) => {
          const active = selectedDelay === option.value.toString();
          return (
            <CustomTooltip
              key={option.value}
              show={Boolean(option.isDisabled && option.disabledTooltip)}
              title={option.disabledTooltip}>
              <div
                key={i}
                className={clsx('trigger-drop-down-option-container', {
                  'trigger-drop-down-option-active': active,
                  'text-disabled': option.isDisabled,
                })}
                onClick={() => {
                  if (option.isDisabled) return;

                  setDelayDropdown(null);
                  setTempCustomDelay(null);
                  setIsCustomDelay(false);
                  setSelectedDelay(option.value?.toString());
                }}>
                <ShortString name={option.label} stringLength={32} placement="left" show={!option.isDisabled} />
                {active ? <TickIcon2 width={14} height={16} color="var(--icon-highlight)" /> : ''}
              </div>
            </CustomTooltip>
          );
        })}
        <div>
          <div className="flex flex-start items-start gap-2">
            <InputComponent
              type="number"
              style={{
                color:
                  tempCustomDelay && selectedDelay === tempCustomDelay
                    ? 'var(--text-highlight)'
                    : 'var(--text-default)',
              }}
              inputClassName={clsx('text-default', {
                'text-highlight': selectedDelay === tempCustomDelay,
              })}
              inputWrapperClass={clsx({
                'border-hover': !isCustomDelay,
                'border-danger': isMaxLimitReachedForCustomDelay || isMinLimitReachedForCustomDelay,
              })}
              value={tempCustomDelay || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempCustomDelay(e.target.value)}
              placeholder="Custom"
              onFocus={() => {
                setIsCustomDelay(true);

                setTimeout(() => {
                  if (customDelaySubmitButtonRef) {
                    customDelaySubmitButtonRef.current?.scrollIntoView({
                      behavior: 'smooth',
                    });
                  }
                }, 500);
              }}
            />

            <div
              className={clsx('text-base font-normal text-subdued border-default rounded p-2', {
                'text-highlight': selectedDelay === tempCustomDelay,
              })}>
              Days
            </div>
          </div>
          {isMaxLimitReachedForCustomDelay && (
            <div className="error-message text-danger text-xs mt-1">{maxCustomDelayErrorMessage}</div>
          )}
          {isMinLimitReachedForCustomDelay && (
            <div className="error-message text-danger text-xs mt-1">{minCustomDelayErrorMessage}</div>
          )}
          {isCustomDelay && (
            <div className="mt-2" ref={customDelaySubmitButtonRef}>
              <ModalCommonButtonsContainer
                submitButtonText="Save"
                onSave={() => {
                  setIsCustomDelay(false);
                  setSelectedDelay(tempCustomDelay || '');
                  setDelayDropdown(null);
                }}
                onClose={() => {
                  setIsCustomDelay(false);
                  setTempCustomDelay(null);
                }}
                cancelButtonText="Cancel"
                disabled={!tempCustomDelay || Number(tempCustomDelay) <= 0 || isMaxLimitReachedForCustomDelay}
              />
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default FrequencyInput;
